import * as React from 'react';
import withStyles from 'react-jss';
import { Carousel, message, Modal, Upload } from 'antd';
import { CloseOutlined, InboxOutlined } from '@ant-design/icons';
import { UploadChangeParam } from 'antd/lib/upload';
import { UploadFile } from 'antd/lib/upload/interface';
import { CssType, ThemeType } from 'src/theme/jss-types';
import PodImagePreviewModal from './PodImagePreviewModal';
import { HocOptions } from './generic-hoc.types';
import GenericHoc from './generic-hoc';
import { getRequiredInvoiceConfig } from 'src/components/pages/Invoices/Invoices.utils';

const { Dragger } = Upload;

const styles = (theme: ThemeType): CssType => ({
  subtext: {
    color: '#666666',
    fontSize: '10px',
    fontWeight: 400,
  },
  modal: {
    '& .ant-modal-close': {
      color: '#111111',
    },
    '& .ant-modal-header': {
      borderBottom: '1px solid #111111',
    },
    '& .ant-modal-footer': {
      borderTop: 0,
    },
  },
});

interface PodUploadModalProps {
  classes: any;
  isVisible: boolean;
  onModalClose: (isVisible: boolean) => void;
  loading: boolean;
  handleSubmit: (fileList: any) => void;
  title: string;
  listType: 'text' | 'picture' | 'picture-card';
  uploadedImageList: UploadFile[];
  consignmentActionConfig: any;
  shouldSendExistingImages?: boolean;
  warningContent?: React.ReactNode;
  pieceStatusData?: any;
  isBulk?: boolean;
  consignmentData?: any;
  beforeUpload?: any;
  t: any;
}

const PodUploadModal = (props: PodUploadModalProps) => {
  const {
    classes,
    isVisible,
    onModalClose,
    loading,
    handleSubmit,
    title,
    listType,
    uploadedImageList,
    consignmentActionConfig,
    shouldSendExistingImages,
    warningContent = null,
    pieceStatusData = {},
    isBulk,
    consignmentData,
    beforeUpload,
    t,
  } = props;
  const [fileList, setFileList] = React.useState(uploadedImageList || [] as UploadFile[]);
  const [imageModalPreviewProps, setImageModalPreviewProps] = React.useState<{
    isVisible: boolean;
    imageList: UploadFile[];
  }>({
    isVisible: false,
    imageList: [],
  });

  type CustomerRequestArgs = {
    onSuccess?: any;
    onError?: any;
    file?: any;
  };

  const requiredConfig = getRequiredInvoiceConfig(
    consignmentActionConfig?.flow_config,
    { consignmentData },
  );

  const maxPodImages = requiredConfig?.[pieceStatusData?.status]?.max_proof_count ?? 1;
  const minPodImages = requiredConfig?.[pieceStatusData?.status]?.min_proof_count ?? 1;

  const customRequest = (args: CustomerRequestArgs) => {
    const { onSuccess, file } = args;
    setTimeout(() => {
      onSuccess(null, file);
    }, 100);
  };

  const uploadProps = {
    accept: '.png, .jpeg, .pdf, .jpg',
    name: 'file',
    multiple: isBulk || maxPodImages > 1,
    customRequest,
    listType,
    showUploadList: {
      showPreviewIcon: true,
      removeIcon: <CloseOutlined />,
    },
  };

  const onPreview = async (file: UploadFile) => {
    setImageModalPreviewProps({
      isVisible: true,
      imageList: [{
        ...file,
        url: file.url || file.thumbUrl,
      }],
    });
  };

  const renderImageModal = () => {
    if (!imageModalPreviewProps.imageList?.length) {
      return null;
    }
    return (
      <PodImagePreviewModal
        isVisible={imageModalPreviewProps.isVisible}
        handleClose={() => setImageModalPreviewProps({
          isVisible: false,
          imageList: [],
        })}
        imageList={imageModalPreviewProps.imageList}
      />
    );
  };

  const handleModalClose = () => {
    onModalClose(false);
  };

  const handleUploadChange = async (info: UploadChangeParam) => {
    if (beforeUpload && info.file && !beforeUpload(info.file)) return;
    const files = info.fileList;
    let filesToShow = [];
    // eslint-disable-next-line no-nested-ternary
    if (files?.length) {
      if (isBulk) {
        filesToShow = files;
      } else if (maxPodImages > 1) {
        filesToShow = files.slice(-maxPodImages);
      } else {
        filesToShow = files.slice(-1);
      }
    }
    setFileList(filesToShow);
  };

  const handleOk = () => {
    let fileListToSend = [];
    if (shouldSendExistingImages) {
      fileList.forEach((file) => {
        if (file.originFileObj) {
          fileListToSend.push(file.originFileObj);
        } else {
          fileListToSend.push(file);
        }
      });
    } else {
      fileListToSend = fileList?.map((f) => f.originFileObj)?.filter((f) => !!f);
    }

    if (fileListToSend.length < minPodImages) {
      message.error(`${t('Please upload at least')} ${minPodImages} ${t('image(s)')}`);
      return;
    }
    handleSubmit(fileListToSend);
  };

  return (
    <Modal
      confirmLoading={loading}
      width={427}
      title={title}
      visible={isVisible}
      maskClosable={false}
      okText={t('Submit')}
      onOk={handleOk}
      className={classes.modal}
      okButtonProps={{
        disabled:
          !fileList.length ||
          loading,
      }}
      onCancel={handleModalClose}
    >
      <Dragger
        fileList={fileList}
        style={{ padding: '16px' }}
        onChange={handleUploadChange}
        onPreview={onPreview}
        {...uploadProps}
      >
        <p style={{ fontSize: '12px' }} />
        <p className="ant-upload-drag-icon">
          <InboxOutlined style={{ color: '#CCCCCC' }} />
        </p>
        <p
          style={{
            color: '#525252',
            fontSize: '14px',
            fontWeight: 400,
          }}
        >
          <b>
            {t('Click or drag file to this area to upload')}
          </b>
          {!isBulk &&
            <div className={classes.subtext}>
              {maxPodImages > 1 ? `${t('maximum')} ${maxPodImages} ${t('allowed')}` : t('Support for a single upload')}
            </div>}
        </p>
      </Dragger>
      {imageModalPreviewProps?.isVisible && renderImageModal()}
      {warningContent}
    </Modal>
  );
};

const mapStateToProps = ({ masterData, deliveryAcknowledgement }) => {
  const { vendor_config } = masterData;
  return {
    deliveryAcknowledgement,
    consignmentActionConfig: vendor_config?.consignment_action_config || {},
  };
};

const hocConfig: HocOptions = {
  connectRedux: {
    useRedux: true,
    mapStateToProps,
  },
  connectJss: {
    useJss: true,
    styleSheet: styles,
  },
  connectRouter: true,
  connectTranslession: true,
};

export default GenericHoc(hocConfig)(PodUploadModal);
